import React from 'react'
import { graphql, Link } from 'gatsby'

import ButtonPrimary from '../components/Links/ButtonPrimary'
import Hero from '../components/Headers/Hero'
import ImageRounded from '../components/Images/ImageRounded'
import Offset from '../components/Layouts/Offset'
import PageWrap from '../components/Layouts/PageWrap'
import SectionContainer from '../components/Layouts/SectionContainer'
import SectionHeader from '../components/Headers/SectionHeader/SectionHeader'
import ThreeUp from '../components/Layouts/ThreeUp'
import WYSIWYG from '../components/WYSIWYG'
import BulletList from '../components/Lists/BulletList/BulletList.js'

const transformData = ({ acfCareers, title }) => {
  if (!acfCareers) return {}
  const {
    heroCopy,
    headerImage,
    jobOpenings,
    cta,
    listSection,
    footerImage,
  } = acfCareers
  const data = {
    hero: {
      heading: heroCopy || '',
      sectionHeader: {
        heading: title || '',
        as: 'h1',
      },
    },
  }
  if (headerImage) {
    data.headerImage = {
      src: headerImage.mediaItemUrl || '',
      alt: headerImage.altText || '',
    }
  }
  if (jobOpenings) {
    data.jobOpeningsSection = {
      sectionHeader: {
        heading: jobOpenings.heading || '',
      },
      wysiwyg: {
        content: jobOpenings.content || '',
      },
      openings: null,
    }
    if (jobOpenings.openings && jobOpenings.openings.length) {
      data.jobOpeningsSection.openings = jobOpenings.openings.map(
        ({ heading, listings }) => {
          return {
            title: heading,
            listings: listings.map(({ heading, link }) => {
              return {
                title: heading,
                link: link.url,
              }
            }),
          }
        }
      )
    }
  }
  if (cta) {
    data.cta = {
      copy: cta.copy || '',
      button: cta.button
        ? {
            to: cta.button.url,
            text: cta.button.title,
          }
        : null,
    }
  }
  if (listSection) {
    data.listSection = {
      heading: listSection.heading || '',
      content: listSection.content || '',
      list:
        listSection.listItems && listSection.listItems.length
          ? listSection.listItems.map(({ item }) => {
              return item
            })
          : null,
    }
  }
  if (footerImage) {
    data.footerImage = {
      src: footerImage.mediaItemUrl,
      alt: footerImage.altText,
      reverse: true,
    }
  }
  return data
}

const Careers = args => {
  const data = transformData(args.data.page)
  return (
    <PageWrap seo={args.data?.page?.seo} noFlex>
      {data.hero && (
        <SectionContainer>
          <Hero size200 {...data.hero} />
        </SectionContainer>
      )}
      {data.headerImage && (
        <SectionContainer as="div">
          <Offset inline>
            <ImageRounded {...data.headerImage} />
          </Offset>
        </SectionContainer>
      )}
      {data.jobOpeningsSection && (
        <SectionContainer className="container">
          <SectionHeader {...data.jobOpeningsSection.sectionHeader} />
          <div className="pt-8 md:pt-12">
            <WYSIWYG {...data.jobOpeningsSection.wysiwyg} />
          </div>
          {data.jobOpeningsSection.openings &&
            data.jobOpeningsSection.openings && (
              <div className="pt-8 md:pt-12">
                <ThreeUp className="pt-8 md:pt-12">
                  {data.jobOpeningsSection.openings.map((opening, index) => (
                    <div
                      className={`${index !==
                        data.jobOpeningsSection.openings.length - 1 &&
                        'md:border md:border-gray'}`}
                      style={{
                        'border-top': 'none',
                        'border-left': 'none',
                        'border-bottom': 'none',
                      }}
                    >
                      <h3 className="uppercase text-sm font-bold pb-6 md:pb-8">
                        {opening.title}
                      </h3>
                      {opening.listings.map((opening, index) => (
                        <a
                          className="text-blue-200 hover:text-blue-100 text-xl md:text-2xl"
                          href={opening.link}
                        >
                          {opening.title}
                        </a>
                      ))}
                    </div>
                  ))}
                </ThreeUp>
              </div>
            )}
        </SectionContainer>
      )}
      {data.cta && (
        <SectionContainer>
          <Offset direction="left">
            <div className="py-24 md:py-40 pr-20 text-white bg-gray-700">
              <Offset rounded={false}>
                {data.cta.copy && (
                  <p className="text-4xl md:text-5xl font-serif font-bold leading-tight">
                    {data.cta.copy}
                  </p>
                )}
                {data.cta.button && (
                  <div className="pt-12 md:pt-16">
                    <ButtonPrimary as={Link} isOnDarkBg {...data.cta.button}>
                      {data.cta.button.text}
                    </ButtonPrimary>
                  </div>
                )}
              </Offset>
            </div>
          </Offset>
        </SectionContainer>
      )}
      {data.listSection && (
        <SectionContainer className="container my-40">
          {data.listSection.heading && (
            <h3 className="text-4xl font-serif font-bold">
              {data.listSection.heading}
            </h3>
          )}
          {data.listSection.content && (
            <div className="pt-4">
              <WYSIWYG content={data.listSection.content} />
            </div>
          )}
          {data.listSection.list && (
            <BulletList>
              {data.listSection.list.map(item => (
                <li>{item}</li>
              ))}
            </BulletList>
          )}
        </SectionContainer>
      )}
      {data.footerImage && (
        <section className="my-40">
          <div className="container">
            <ImageRounded {...data.footerImage} />
          </div>
        </section>
      )}
    </PageWrap>
  )
}

export default Careers

export const query = graphql`
  query($id: String) {
    page: wpPage(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
      title
      acfCareers {
        cta {
          copy
          button {
            title
            url
          }
        }
        footerImage {
          altText
          mediaItemUrl
        }
        headerImage {
          altText
          mediaItemUrl
        }
        heroCopy
        jobOpenings {
          content
          heading
          openings {
            heading
            listings {
              heading
              link {
                target
                title
                url
              }
            }
          }
        }
        listSection {
          content
          heading
          listItems {
            item
          }
        }
      }
    }
  }
`
